import { Image } from '~/types/Product';

type ImageSize = 'original' | 'small_thumbnail' | 'large_thumbnail';

const ABSOLUTE_URL_PATTERN = /^(https?:)?\/\//i;

/**
 * Resolves a rich-editor image value (CmsImage / CmsVideo poster) to a usable src.
 *
 * Rich-editor image fields historically stored relative gallery paths that must be
 * routed through the Liip Imagine cache filter, prefixed with the API base URL.
 * Newer values (direct uploads, some gallery picks) are stored as absolute URLs and
 * must be used as-is — prefixing the cache path onto an absolute URL produces a
 * broken `.../media/cache/monsieurbiz_rich_editor_image/https://...` src (ANK-127).
 *
 * Only `http://`, `https://` and protocol-relative `//` values are treated as
 * absolute; anything else (including `javascript:`/`data:`) keeps the legacy
 * relative-path behavior.
 */
export function resolveRichEditorImageSrc(apiUrl: string | undefined, image: string): string {
  if (ABSOLUTE_URL_PATTERN.test(image)) {
    return image;
  }
  return `${apiUrl}/media/cache/monsieurbiz_rich_editor_image/${image}`;
}

const SIZE_KEYS: Record<ImageSize, string> = {
  original: 'sylius_shop_product_original',
  small_thumbnail: 'sylius_shop_product_small_thumbnail',
  large_thumbnail: 'sylius_shop_product_large_thumbnail',
};

export function getProductImages(
  images: Image[] | Record<string, Image[]> | undefined,
  size: ImageSize = 'large_thumbnail'
): Image[] {
  if (!images) {
    return [];
  }
  if (Array.isArray(images)) {
    return images;
  }
  return images[SIZE_KEYS[size]] ?? [];
}

/**
 * Returns the image flagged as `type: 'main'`, falling back to the first image.
 */
export function getMainProductImage(
  images: Image[] | Record<string, Image[]> | undefined,
  size: ImageSize = 'large_thumbnail'
): Image | undefined {
  const list = getProductImages(images, size);
  return list.find((img) => img.type === 'main') ?? list[0];
}

/**
 * Builds a `srcset` string from an image's small/medium thumbnails (300w/500w).
 * Returns undefined when `thumbnails` is absent so callers can fall back to a plain `src`.
 */
export function getProductImageSrcSet(image: Image | undefined): string | undefined {
  if (!image?.thumbnails) {
    return undefined;
  }
  return `${image.thumbnails.small} 300w, ${image.thumbnails.medium} 500w`;
}

/**
 * Filter images based on variant code.
 * An image is shown if:
 * - variantCodes is empty/undefined (show for all variants)
 * - variantCodes contains the selected variant code
 */
export function filterImagesByVariant(
  images: Image[] | Record<string, Image[]> | undefined,
  variantCode: string | undefined
): Image[] | Record<string, Image[]> {
  if (!images) {
    return [];
  }

  const filterFn = (img: Image) =>
    !img.variantCodes || img.variantCodes.length === 0 || (variantCode && img.variantCodes.includes(variantCode));

  if (Array.isArray(images)) {
    return images.filter(filterFn);
  }

  // For Record<string, Image[]>, filter each size category
  const result: Record<string, Image[]> = {};
  for (const [key, imageArray] of Object.entries(images)) {
    result[key] = imageArray.filter(filterFn);
  }
  return result;
}
